import { useState, useEffect } from 'react';
import { Search, Calendar, X, Loader2, Clock } from 'lucide-react';
import { fetchUpcomingMeetings, fetchAttendedMeetings } from '../../services/salesCoachContextService';
import { toLocaleDateStringInTz } from '../../utils/dateFormatters';
import { useTimezone } from '../../hooks/useTimezone';

interface MeetingSelectorProps {
  mode: 'upcoming' | 'attended';
  onSelect: (meeting: any) => void;
  onClose: () => void;
}

export function MeetingSelector({ mode, onSelect, onClose }: MeetingSelectorProps) {
  const tz = useTimezone();
  const [meetings, setMeetings] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = mode === 'upcoming'
        ? await fetchUpcomingMeetings()
        : await fetchAttendedMeetings();
      setMeetings(data);
      setFiltered(data);
      setLoading(false);
    };
    load();
  }, [mode]);

  useEffect(() => {
    if (!search.trim()) {
      setFiltered(meetings);
      return;
    }
    const q = search.toLowerCase();
    setFiltered(
      meetings.filter(
        (m) =>
          m.company_name?.toLowerCase().includes(q) ||
          m.dm_full_name?.toLowerCase().includes(q)
      )
    );
  }, [search, meetings]);

  const formatDate = (date: string) => {
    try {
      return toLocaleDateStringInTz(date, tz, 'en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  };

  const formatTime = (time: string) => {
    if (!time) return '';
    try {
      const [h, m] = time.split(':');
      const hour = parseInt(h);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const h12 = hour % 12 || 12;
      return `${h12}:${m} ${ampm}`;
    } catch {
      return time;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden border border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1875FF]/5 to-[#05E0D1]/5">
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">
              {mode === 'upcoming' ? 'Select Upcoming Meeting' : 'Select Past Meeting'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {mode === 'upcoming'
                ? 'Choose a meeting to prepare for'
                : 'Choose a meeting to review'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="px-6 pt-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by company or contact..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#1875FF] focus:ring-2 focus:ring-[#1875FF]/20 transition-all text-sm"
              autoFocus
            />
          </div>
        </div>

        <div className="px-4 pb-4 max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-[#1875FF] animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm font-medium">
                {search ? 'No meetings match your search' : `No ${mode} meetings found`}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((meeting) => (
                <button
                  key={meeting.meeting_id}
                  onClick={() => onSelect(meeting)}
                  className="w-full flex items-center gap-4 p-3.5 rounded-xl border border-gray-100 hover:border-[#1875FF]/40 hover:bg-[#1875FF]/5 transition-all text-left group"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 flex flex-col items-center justify-center">
                    <span className="text-xs font-bold text-[#1875FF] leading-none">
                      {formatDate(meeting.meeting_date).split(',')[0]?.split(' ')[1]}
                    </span>
                    <span className="text-lg font-bold text-[#1875FF] leading-none mt-0.5">
                      {formatDate(meeting.meeting_date).split(' ').pop()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm truncate group-hover:text-[#1875FF] transition-colors">
                      {meeting.company_name}
                    </div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">
                      {meeting.dm_full_name}{meeting.dm_title ? ` - ${meeting.dm_title}` : ''}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Clock size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-400">
                        {formatDate(meeting.meeting_date)} at {formatTime(meeting.meeting_time)}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 font-medium capitalize">
                      {meeting.booked_channel || 'N/A'}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
